import { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { AppDispatch, RootState } from 'store'
import {
  acknowledgeNotificationCenter,
  getNotificationCenter,
  markAllNotificationCenterRead,
  markNotificationCenterRead,
} from 'features/notificationCenter/notificationCenterSlice'
import { INotificationItem } from 'types/notificationCenter.interface'
import { ISelectValue } from 'constants/globalTypes'
import { NavBar } from 'components/NavBar/NavBar'
import { Button } from 'components/ui/Button/Button'
import { StatusPill } from 'components/ui/StatusPill/StatusPill'
import { HonestSelectInput } from 'components/HonestSelect/HonestSelectInput'
import { JustSpinner } from 'components/Loading/Loading'
import { PageWrapper, Flex, Tabs, TabsContainer } from 'styling/GlobalStyles'
import { Head1, OtherText } from 'styling/Typography'
import { tokens } from 'styling/tokens'
import { timeAgo } from 'utils/helpers'
import useAuth from 'hooks/useAuth'
import {
  MODULE_CATEGORIES,
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_MODULES,
  notificationTypeDef,
} from './notificationRegistry'
import {
  ControlsRow,
  ControlsSpacer,
  NotifList,
  NotifRow,
  SevDot,
  NotifBody,
  NotifTitle,
  NotifDetail,
  NotifMeta,
  NotifSide,
  AckedTag,
  EmptyState,
  UnreadSummary,
} from './NotificationCenter.styled'

const SEV_PILL: { [sev: string]: 'danger' | 'warning' | 'info' } = {
  high: 'danger',
  med: 'warning',
  low: 'info',
}

export const NotificationCenter = () => {
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { items, unreadCount, isLoading } = useSelector((state: RootState) => state.notificationCenter)
  const { user } = useAuth()
  const [tab, setTab] = useState<'all' | 'unread' | 'critical'>('all')
  const [module, setModule] = useState('all')
  const [category, setCategory] = useState('all')
  const [severity, setSeverity] = useState('all')

  useEffect(() => {
    dispatch(getNotificationCenter({ roleId: user?.currentContract?.role?.id ?? null }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch])

  const filtered = useMemo(
    () =>
      items.filter((n) => {
        if (tab === 'unread' && n.read) return false
        if (tab === 'critical' && !(n.requiresAck && !n.acknowledged)) return false
        if (module !== 'all' && notificationTypeDef(n.type).module !== module) return false
        if (category !== 'all' && n.category !== category) return false
        if (severity !== 'all' && n.severity !== severity) return false
        return true
      }),
    [items, tab, module, category, severity],
  )

  const criticalOpen = useMemo(() => items.filter((n) => n.requiresAck && !n.acknowledged).length, [items])

  const openItem = (n: INotificationItem) => {
    if (!n.read) dispatch(markNotificationCenterRead(n.id))
    if (n.link) navigate(n.link)
  }

  return (
    <>
      <NavBar />
      <PageWrapper noFooter>
        <Flex between center mb='16' style={{ minHeight: '2.5rem' }}>
          <Head1 style={{ fontSize: '1.5rem', lineHeight: 1.3 }} color={tokens.text.primary}>
            Alerts & Notifications
          </Head1>
          <Button variant='secondary' onClick={() => dispatch(markAllNotificationCenterRead())}>
            Mark all read
          </Button>
        </Flex>
        <OtherText grey>
          Alerts, requests and reminders across all modules · critical alerts require acknowledgement
        </OtherText>

        <ControlsRow>
          <TabsContainer>
            <Tabs active={tab === 'all'} onClick={() => setTab('all')}>
              All
            </Tabs>
            <Tabs active={tab === 'unread'} onClick={() => setTab('unread')}>
              Unread ({unreadCount})
            </Tabs>
            <Tabs active={tab === 'critical'} onClick={() => setTab('critical')}>
              Needs acknowledgement ({criticalOpen})
            </Tabs>
          </TabsContainer>
          <ControlsSpacer />
          <HonestSelectInput
            width='180px'
            nomarg
            labelText='Module'
            options={[
              { value: 'all', label: 'All modules' },
              ...NOTIFICATION_MODULES.map((m) => ({ value: m.key, label: m.name })),
            ]}
            defaultValue='all'
            onChange={(v: ISelectValue) => {
              setModule(String(v.value))
              setCategory('all')
            }}
          />
          <HonestSelectInput
            key={module}
            width='220px'
            nomarg
            labelText='Category'
            options={[
              { value: 'all', label: 'All categories' },
              ...(module === 'all' ? NOTIFICATION_CATEGORIES : MODULE_CATEGORIES[module] || []).map(
                (c) => ({ value: c, label: c }),
              ),
            ]}
            defaultValue='all'
            onChange={(v: ISelectValue) => setCategory(String(v.value))}
          />
          <HonestSelectInput
            width='150px'
            nomarg
            labelText='Severity'
            options={[
              { value: 'all', label: 'All' },
              { value: 'high', label: 'High' },
              { value: 'med', label: 'Med' },
              { value: 'low', label: 'Low' },
            ]}
            defaultValue='all'
            onChange={(v: ISelectValue) => setSeverity(String(v.value))}
          />
        </ControlsRow>

        <UnreadSummary>
          {filtered.length} shown · {unreadCount} unread
        </UnreadSummary>

        {isLoading && !items.length ? (
          <Flex justcenter mt='32'>
            <JustSpinner />
          </Flex>
        ) : !filtered.length ? (
          <EmptyState>
            <OtherText grey>Nothing here — you’re all caught up.</OtherText>
          </EmptyState>
        ) : (
          <NotifList>
            {filtered.map((n) => (
              <NotifRow key={n.id} $unread={!n.read} $clickable={!!n.link} onClick={() => openItem(n)}>
                <SevDot $sev={n.severity} />
                <NotifBody>
                  <NotifTitle $unread={!n.read}>{n.title}</NotifTitle>
                  <NotifDetail>{n.detail}</NotifDetail>
                  <NotifMeta>
                    <StatusPill status={SEV_PILL[n.severity]}>{n.severity.toUpperCase()}</StatusPill>
                    <span>{n.category}</span>
                    {n.siteName && <span>· {n.siteName}</span>}
                    <span>· {timeAgo(n.createdAt) || 'just now'}</span>
                  </NotifMeta>
                </NotifBody>
                <NotifSide>
                  {n.requiresAck &&
                    (n.acknowledged ? (
                      <AckedTag>Acknowledged ✓</AckedTag>
                    ) : (
                      <Button
                        variant='primary'
                        size='sm'
                        onClick={(e: any) => {
                          e.stopPropagation()
                          dispatch(acknowledgeNotificationCenter(n.id))
                        }}
                      >
                        Acknowledge
                      </Button>
                    ))}
                </NotifSide>
              </NotifRow>
            ))}
          </NotifList>
        )}
      </PageWrapper>
    </>
  )
}
